import * as Tone from 'tone';
import type { LayerConfig, LayerType, GenerationParams } from '../types';
import { emitGrainTrigger } from '../ui/activity-meters';

/**
 * ADSR envelope configuration
 */
export interface ADSRConfig {
  attack: number;  // seconds
  decay: number;   // seconds
  sustain: number; // 0-1 level
  release: number; // seconds
}

/**
 * GrainLayer - Individual layer with grain synthesis and ADSR envelope
 * Each layer has its own grain player, envelope, filter, and gain
 */
export class GrainLayer {
  private type: LayerType;
  private grainPlayer: Tone.GrainPlayer;
  private envelope: Tone.AmplitudeEnvelope;
  private filter: Tone.Filter;
  private gain: Tone.Gain;
  private config: LayerConfig;
  private adsr: ADSRConfig;

  constructor(type: LayerType, output: Tone.ToneAudioNode) {
    this.type = type;
    this.config = this.getDefaultConfig(type);
    this.adsr = this.getDefaultADSR(type);

    // Create grain player
    this.grainPlayer = new Tone.GrainPlayer({
      grainSize: this.config.grainSize / 1000, // Convert ms to seconds
      overlap: 0.5, // 50% overlap
      loop: false, // Changed to false for triggered playback
      playbackRate: 1,
    });

    // Create amplitude envelope for click-free playback
    this.envelope = new Tone.AmplitudeEnvelope({
      attack: this.adsr.attack,
      decay: this.adsr.decay,
      sustain: this.adsr.sustain,
      release: this.adsr.release,
    });

    // Create filter
    this.filter = new Tone.Filter({
      type: this.config.filter.type,
      frequency: this.config.filter.frequency,
      Q: this.config.filter.Q,
    });

    // Create gain (volume control)
    this.gain = new Tone.Gain(0.25); // 25% volume per layer (4 layers = 100%)

    // Signal chain: GrainPlayer → Envelope → Filter → Gain → Output
    this.grainPlayer.connect(this.envelope);
    this.envelope.connect(this.filter);
    this.filter.connect(this.gain);
    this.gain.connect(output);
  }

  /**
   * Get default ADSR settings per layer type
   * Tuned to reduce clicks while preserving character
   */
  private getDefaultADSR(type: LayerType): ADSRConfig {
    switch (type) {
      case 'percussion':
        // Quick attack for punch, short decay, minimal sustain
        return { attack: 0.002, decay: 0.05, sustain: 0.3, release: 0.02 };
      case 'bass':
        // Slightly slower attack to reduce sub-bass clicks
        return { attack: 0.008, decay: 0.1, sustain: 0.7, release: 0.05 };
      case 'melody':
        // Smooth attack/release for melodic content
        return { attack: 0.005, decay: 0.08, sustain: 0.6, release: 0.04 };
      case 'pad':
        // Slow attack/release for ambient, smooth pads
        return { attack: 0.02, decay: 0.15, sustain: 0.8, release: 0.1 };
    }
  }

  /**
   * Set ADSR envelope parameters
   */
  setADSR(adsr: Partial<ADSRConfig>): void {
    if (adsr.attack !== undefined) {
      this.adsr.attack = adsr.attack;
      this.envelope.attack = adsr.attack;
    }
    if (adsr.decay !== undefined) {
      this.adsr.decay = adsr.decay;
      this.envelope.decay = adsr.decay;
    }
    if (adsr.sustain !== undefined) {
      this.adsr.sustain = adsr.sustain;
      this.envelope.sustain = adsr.sustain;
    }
    if (adsr.release !== undefined) {
      this.adsr.release = adsr.release;
      this.envelope.release = adsr.release;
    }
  }

  /**
   * Get current ADSR settings
   */
  getADSR(): ADSRConfig {
    return { ...this.adsr };
  }

  /**
   * Set audio buffer for this layer
   */
  setBuffer(buffer: Tone.ToneAudioBuffer): void {
    this.grainPlayer.buffer = buffer;
  }

  /**
   * Trigger a single grain at specific time with ADSR envelope
   * @param time - Scheduled time (Tone.js time)
   * @param pitch - MIDI note number (60 = middle C, 0 = original pitch)
   */
  trigger(time: number, pitch = 0): void {
    // Calculate playback rate from pitch
    const playbackRate = pitch === 0 ? 1 : Math.pow(2, pitch / 12);
    this.grainPlayer.playbackRate = playbackRate;

    // Calculate total envelope + grain duration
    const grainDuration = Number(this.grainPlayer.grainSize);
    const envelopeDuration = this.adsr.attack + this.adsr.decay + grainDuration;

    // Trigger grain at scheduled time
    this.grainPlayer.start(time);

    // Trigger envelope attack at the same time
    this.envelope.triggerAttack(time);

    // Schedule envelope release to create smooth fade out
    const releaseTime = time + envelopeDuration - this.adsr.release;
    this.envelope.triggerRelease(releaseTime);

    // Auto-stop grain player after full envelope cycle
    this.grainPlayer.stop(time + envelopeDuration + this.adsr.release);

    // Emit event for activity meters
    setTimeout(() => emitGrainTrigger(this.type, 1), 0);
  }

  /**
   * Start playback (continuous - for legacy compatibility)
   */
  start(): void {
    console.warn('⚠️ Continuous playback deprecated, use trigger() instead');
    this.grainPlayer.loop = true;
    this.grainPlayer.start();
  }

  /**
   * Stop playback
   */
  stop(): void {
    this.grainPlayer.stop();
  }

  /**
   * Update grain parameters from generation params
   */
  updateFromParams(params: GenerationParams): void {
    // Density affects grain size and playback rate
    const densityScale = params.density / 100;

    // Adjust grain size based on density
    // Higher density = more grains = smaller grain size
    const baseGrainSize = this.config.grainSize;
    const newGrainSize = baseGrainSize * (1 - densityScale * 0.5); // 50-100% of base
    this.grainPlayer.grainSize = newGrainSize / 1000;

    // TODO: Will add tempo/variation params in later days
  }

  /**
   * Set layer volume
   */
  setVolume(volume: number): void {
    this.gain.gain.rampTo(volume, 0.1); // 100ms ramp to avoid clicks
  }

  /**
   * Get default configuration per layer type
   */
  private getDefaultConfig(type: LayerType): LayerConfig {
    switch (type) {
      case 'percussion':
        return {
          grainSize: 10, // Short grains = glitchy, percussive
          density: 30,
          filter: {
            type: 'highpass',
            frequency: 800, // Remove low end
            Q: 1,
          },
          rhythm: [],
        };

      case 'bass':
        return {
          grainSize: 40, // Medium grains
          density: 10,
          filter: {
            type: 'lowpass',
            frequency: 200, // Only low frequencies
            Q: 1,
          },
          rhythm: [],
        };

      case 'melody':
        return {
          grainSize: 30, // Medium grains
          density: 15,
          filter: {
            type: 'bandpass',
            frequency: 1500, // Mid-range (800-3kHz centered at 1.5kHz)
            Q: 1,
          },
          rhythm: [],
        };

      case 'pad':
        return {
          grainSize: 80, // Long grains = smooth, sustained
          density: 50,
          filter: {
            type: 'highpass',
            frequency: 3000, // High frequencies only (air, brightness)
            Q: 1,
          },
          rhythm: [],
        };
    }
  }

  /**
   * Dispose of audio nodes (cleanup)
   */
  dispose(): void {
    this.grainPlayer.dispose();
    this.envelope.dispose();
    this.filter.dispose();
    this.gain.dispose();
  }

  /**
   * Get grain player for direct access (if needed)
   */
  getGrainPlayer(): Tone.GrainPlayer {
    return this.grainPlayer;
  }

  /**
   * Get layer type
   */
  getType(): LayerType {
    return this.type;
  }

  /**
   * Get gain node for recording
   */
  getGainNode(): Tone.Gain {
    return this.gain;
  }
}
